import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReferralLetters, type ReferralLetterListItem } from "@/lib/queries";
import { useDeleteReferralLetter } from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash } from "lucide-react";
import { fmtDate } from "@emr/shared/india";
import { LETTER_TYPE_LABELS, type LetterType } from "@emr/shared/constants";
import { AiBadge, CloudBadge, SentBadge } from "@/components/StatusBadge";

type TabKey = LetterType; // "Incoming" | "Outgoing"

const TAB_STORAGE_KEY = "emr:referrals:tab";

function readPersistedTab(): TabKey {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    if (raw === "Incoming" || raw === "Outgoing") return raw;
  } catch {
    // Non-fatal — localStorage may be unavailable.
  }
  return "Outgoing"; // default to "Refer To"
}

function writePersistedTab(t: TabKey) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, t);
  } catch {
    // Non-fatal.
  }
}

export default function ReferralsPage() {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("referrals.create");
  const canDelete = isAdmin || permissions.has("referrals.delete");
  const { data, isLoading } = useReferralLetters();
  const deleteMut = useDeleteReferralLetter();
  const rows = data?.rows ?? [];

  const [tab, setTab] = useState<TabKey>(() => readPersistedTab());

  const incoming = rows.filter((r) => r.letterType === "Incoming");
  const outgoing = rows.filter((r) => r.letterType === "Outgoing");
  const visible = tab === "Incoming" ? incoming : outgoing;

  const onTabChange = (t: string) => {
    if (t === "Incoming" || t === "Outgoing") {
      setTab(t);
      writePersistedTab(t);
    }
  };

  const newLetterType = tab; // "New" button defaults to the active tab's type

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Referral Letters</h1>
        {canCreate && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/referrals/reply/new")}
            >
              <Plus size={16} className="mr-1" /> New Referral Reply
            </Button>
            <Button
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
              onClick={() => navigate("/referrals/to/new")}
            >
              <Plus size={16} className="mr-1" /> New Referral
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="Outgoing">Refer To ({outgoing.length})</TabsTrigger>
          <TabsTrigger value="Incoming">Referral Reply ({incoming.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="Outgoing">
          <ReferralTable
            rows={outgoing}
            visibleRows={visible}
            tab="Outgoing"
            isLoading={isLoading}
            canDelete={canDelete}
            onDelete={(id) => deleteMut.mutate(id)}
            onEdit={(id) => navigate(`/referrals/to/${id}`)}
          />
        </TabsContent>

        <TabsContent value="Incoming">
          <ReferralTable
            rows={incoming}
            visibleRows={visible}
            tab="Incoming"
            isLoading={isLoading}
            canDelete={canDelete}
            onDelete={(id) => deleteMut.mutate(id)}
            onEdit={(id) => navigate(`/referrals/reply/${id}`)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReferralTable({
  rows,
  visibleRows,
  tab,
  isLoading,
  canDelete,
  onDelete,
  onEdit,
}: {
  rows: ReferralLetterListItem[];
  visibleRows: ReferralLetterListItem[];
  tab: TabKey;
  isLoading: boolean;
  canDelete: boolean;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
              <TableHead>Patient</TableHead>
              <TableHead>UHID</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>{tab === "Incoming" ? "From (referring doctor)" : "Subject / Recipient"}</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Skeleton className="h-10" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted py-8">
                  No {LETTER_TYPE_LABELS[tab].toLowerCase()} letters
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((r: ReferralLetterListItem) => (
                <TableRow
                  key={r.id}
                  className="even:bg-cream-50/50 cursor-pointer"
                  onClick={() => onEdit(r.id)}
                >
                  <TableCell className="font-medium">{r.patientName ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.clinicUhid ?? "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(r.createdAt)}</TableCell>
                  <TableCell className="text-xs text-muted">
                    {tab === "Outgoing" ? (r.subject ?? "—") : (r.doctorName ?? "—")}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.aiPolished && <AiBadge />}
                      {r.savedToR2 && <CloudBadge />}
                      {r.sentViaPabbly && <SentBadge />}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(r.id)}>
                        <Pencil size={14} />
                      </Button>
                      {canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-500"
                          onClick={() => onDelete(r.id)}
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
